import config from "config";
import Query from "@arcgis/core/rest/support/Query";

const HTTP_CODE_TRANSLATE = {
  0: "Failed to contact the server, probably due to the network issue.",
  429: "Too many request for the resource at the moment",
  404: "Requested resource is not found",
  403: "You don't have access to the resource",
  401: "Failed to authorize",
  500: "The web service is unable to process the request",
  504: "Gatway timeout when waiting for the reply from the web service",
};

class RequestUtils {
  static handleError(debugInfo, callerRef, reject) {
    let { httpCode } = debugInfo;
    httpCode = httpCode || 0;
    let httpMsg = HTTP_CODE_TRANSLATE[httpCode];
    let ex = new Error(`${httpCode ? httpCode + " " : ""}${httpMsg}`);
    ex.httpMsg = httpMsg;
    ex.cause = debugInfo;

    callerRef.props?.showDialog &&
      callerRef.props.showDialog({
        isOpen: true,
        type: "error",
        title: "Error occurred when fetching data",
        content: debugInfo.url,
        jsxElement: ex.message,
        onDialogOk: () => {},
      });
    reject && reject(ex);
    throw ex; // Throw the exception so that caller can handle it
  }
  /**
   * Start a HTTP request with XMLHttpRequest. Useful when you have to send Form Data (x-www-form-urlencoded)
   * @param {*} url URL
   * @param {*} method Method
   * @param {*} bodyInJson Data to send. If it is an object, it concatenates every key-value and make an encoded uri string; if it's a string, just send the string directly
   * @param {*} forCredential Include credential, like cookies, authorization header..etc
   * @returns A Promise
   */
  static async makeHttpQuery(url, method, bodyInJson, forCredential) {
    let callerRef = this;
    return new Promise((resolve, reject) => {
      var sb = [];
      var body = "";
      if (typeof bodyInJson === "object") {
        for (var key in bodyInJson) {
          sb.push(key + "=" + escape(bodyInJson[key]));
        }
        body = sb.join("&");
      } else if (typeof bodyInJson === "string") {
        body = bodyInJson;
      }
      var xmlGet = new XMLHttpRequest();
      xmlGet.open(method, url);
      xmlGet.setRequestHeader(
        "Content-Type",
        typeof bodyInJson === "object"
          ? "application/x-www-form-urlencoded"
          : "application/json"
      );
      xmlGet.responseType = "text";
      if (forCredential) {
        xmlGet.withCredentials = forCredential;
        xmlGet.setRequestHeader("X-Requested-With", null);
      }
      xmlGet.onerror = function() {
        // Abnormal HTTP status IS NOT an error
        // Disconnection / DNS error IS an error
        console.debug("XML Request Error!");
        RequestUtils.handleError(
          {
            url,
            method,
          },
          callerRef,
          reject
        );
      };
      xmlGet.onload = function(evt) {
        if (xmlGet.status != 200)
          RequestUtils.handleError(
            {
              httpCode: xmlGet.status,
              url,
              method,
            },
            callerRef,
            reject
          );
        else resolve(xmlGet.responseText);
      }.bind(this);
      xmlGet.send(body);
    });
  }
  /**
   * Start a HTTP request with fetch API. This function is mainly for sending JSON
   * @param {*} url URL
   * @param {*} method Method
   * @param {*} queryPara The JSON
   * @param {*} queryOption Fetch API options
   * @returns
   */
  static async fetchQueryMain(
    url,
    method,
    queryPara = null,
    queryOption = {},
    contentType = "application/json"
  ) {
    let callerRef = this;
    let headers = {};
    if (queryOption.headers) {
      headers = queryOption.headers;
      delete queryOption.headers;
    }
    let tmpQueryPara = {
      method,
      headers: {
        "Content-Type": contentType,
        ...headers,
      },
      ...queryOption,
    };
    if (
      url.indexOf(config.portalUrl) > -1 ||
      url.indexOf(config.webSrvUrl) > -1
    )
      tmpQueryPara.credentials = "include";
    if (queryPara && method != "GET") {
      if (contentType === "application/json")
        queryPara = JSON.stringify(queryPara);
      tmpQueryPara.body = queryPara;
    }

    return fetch(url, tmpQueryPara)
      .then(function(result) {
        console.debug("Result Data", result);
        if (!result.ok) {
          RequestUtils.handleError(
            {
              httpCode: result.status,
              url,
              method,
            },
            callerRef
          );
        }
        return result;
      })
      .catch((ex) => {
        // If cause exists, it's the error we thrown
        if (!ex.cause) {
          RequestUtils.handleError(
            {
              url,
              method,
            },
            callerRef
          );
        } else throw ex;
      });
  }
  static async fetchQueryByGET(url) {
    let tmpResult = await this.fetchQueryMain(url, "GET");
    return tmpResult;
  }
  static async fetchQueryByPOST(url, queryPara) {
    let tmpResult = await this.fetchQueryMain(url, "POST", queryPara);
    return tmpResult;
  }
  static async nativeFeatureLayerQuery(layerObj, queryPara) {
    let query = new Query();
    Object.keys(queryPara).forEach((key) => {
      switch (key) {
        case "where":
        case "returnGeometry":
        case "outFields":
        case "geometry":
        case "spatialRelationship":
        case "units":
        case "orderByFields":
        case "start":
        case "num":
          query[key] = queryPara[key];
          break;
        default:
          break;
      }
    });
    query.outSpatialReference = {
      wkid: 102140,
    };
    console.log("queryFeatures", query);
    let queryGeoResults = await layerObj.queryFeatures(query);
    return queryGeoResults;
  }
}

export default RequestUtils;
